// Single source of truth for every feature page in /product/*.
// Adding a new feature = add an entry here. Pages are rendered by <FeaturePageTemplate slug={...}>.
//
// Each entry has:
//   slug:       URL segment under /product/
//   nav:        short label for menus ("Jobs")
//   category:   grouping for the mega-menu ("Core Ops" | "Portals" | "Supplier" | "Management" | "Intelligence")
//   title:      page hero headline
//   eyebrow:    tiny uppercase label above the headline
//   subtitle:   hero subheading paragraph
//   outcomes:   bullet list of concrete customer wins (3-5)
//   sections:   array of alternating feature sections (text+screenshot or text+diagram)
//   screenshot: path under /marketing-shots/ (auto-captured later) or a placeholder spec
//   related:    slugs of related features for cross-linking at bottom
//   cta:        final-CTA label override (else defaults to "Start free trial")

export const featureManifest = [
  // ──────────────── CORE OPS ────────────────
  {
    slug: "jobs",
    nav: "Jobs & Work Orders",
    category: "Core Ops",
    eyebrow: "Operations core",
    title: "Every job, one thread.",
    subtitle:
      "From request to completion — schedule, assign, track, and close every work order with full audit trail. Built for teams running hundreds of concurrent jobs without losing a single detail.",
    outcomes: [
      "Create jobs from scratch or promote a quote in one click",
      "Assign to in-house operatives or external vendors with one picker",
      "Auto-attach job sheets from your template library",
      "Line-item costings that flow straight into invoices",
      "Priority flags, SLA timers, and push alerts keep teams honest",
    ],
    sections: [
      {
        kind: "zig",
        headline: "A single workflow from request to sign-off.",
        body: "Every job carries its full lineage — the originating request, approved quote, assigned supplier or operative, attached job sheet, completed timestamps, and signed completion certificate. Auditors love it.",
        screenshotSlug: "jobs-detail",
        screenshotCaption: "Job detail — Schedule | Supplier | In-House Operative",
      },
      {
        kind: "zag",
        headline: "One picker. Vendors or your own team.",
        body: "The `InHouseOperativeAssigner` means you never have to decide between 'managing vendors' or 'managing your team' — Fixlr handles both paths through the same job record.",
        screenshotSlug: "jobs-assign",
        screenshotCaption: "Assign flow with both supplier and operative selectors",
      },
      {
        kind: "zig",
        headline: "Costings, line by line.",
        body: "Quotes roll into jobs and become draft invoice line items you can edit before billing. Markup A (your margin) and Markup B (client surcharge) are configurable per quote.",
        screenshotSlug: "jobs-costings",
        screenshotCaption: "Cost-line editor with live totals",
      },
    ],
    related: ["quotes", "scheduling", "invoicing", "job-sheets"],
  },

  {
    slug: "emergency",
    nav: "Emergency Response",
    category: "Core Ops",
    eyebrow: "Premium differentiator",
    title: "Every minute matters. We track them all.",
    subtitle:
      "A parallel track for out-of-hours, urgent, and safety-critical jobs with hard SLAs, auto-escalation, and a red-alert channel in Slack/Teams. This is the reason large accounts pick Fixlr.",
    outcomes: [
      "Separate `emergency_jobs` pipeline with red-flag styling across the platform",
      "Built-in SLA watcher + `emergency_escalation_scheduler`",
      "Auto-notify on-call operatives via push + SMS (optional Twilio)",
      "Acknowledge / Accept / Resolve workflow with full audit trail",
      "Emergency analytics — median response time, first-time-fix rate",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Red-alert, from the second it's created.",
        body: "Emergency jobs fire through a dedicated scheduler. Operative gets a push, admin gets a Slack ping, and the SLA timer starts. Every acknowledgement is stamped.",
        screenshotSlug: "emergency-dashboard",
      },
      {
        kind: "zag",
        headline: "Escalation ladders that actually fire.",
        body: "If the first assignee doesn't acknowledge within your configured window, the job auto-escalates to the next tier. No more 3am phone-tag.",
        screenshotSlug: "emergency-escalation",
      },
    ],
    related: ["jobs", "in-house", "integrations"],
  },

  {
    slug: "quotes",
    nav: "Quotes → Jobs → Projects",
    category: "Core Ops",
    eyebrow: "Quote-to-cash",
    title: "Quotes that turn into jobs. Or projects. Or both.",
    subtitle:
      "Win the work, click once, become a job or project. Quote templates, line items, markup ladders, client-approval links — all in one pipeline.",
    outcomes: [
      "Quote templates library with VAT-aware pricing",
      "Convert any approved quote into a job (1-to-1) or project (multi-task)",
      "Send branded PDF quotes with signed approval link",
      "Compare supplier quotes side-by-side (supplier comparison portal)",
      "Markup labels are tenant-customizable (e.g. 'PM fee' vs 'Client uplift')",
    ],
    sections: [
      {
        kind: "zig",
        headline: "From template to signed approval in 90 seconds.",
        body: "Pick a template, swap client + property, tweak line items, send. Client gets a secure branded link. They sign, you convert to job. Done.",
        screenshotSlug: "quotes-builder",
      },
      {
        kind: "zag",
        headline: "Side-by-side supplier comparison.",
        body: "Invite multiple suppliers to quote the same request. Compare price, rating, availability, compliance on a single screen. Pick a winner.",
        screenshotSlug: "quotes-comparison",
      },
    ],
    related: ["jobs", "invoicing", "supplier-portal", "ai-supplier-matching"],
  },

  {
    slug: "scheduling",
    nav: "Scheduling & Calendar",
    category: "Core Ops",
    eyebrow: "Time is the grid",
    title: "See every job, every operative, every day.",
    subtitle:
      "Drag-and-drop calendar for admins. Live schedule for operatives. Automatic conflict detection. Weekend cover. Bank holiday awareness.",
    outcomes: [
      "Day / Week / Month calendar views",
      "Drag jobs onto operatives' rows to reassign",
      "Conflict detection — double-booked operatives flag instantly",
      "Routing-aware — Fixlr nudges you when two jobs in the same postcode could be a single visit",
      "Per-tenant timezone so briefs fire at 06:30 LOCAL time, not UTC",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Grid view your field ops can live in.",
        body: "The schedule grid renders every operative as a row and every hour as a column. Drag, drop, done. Operatives see their own version the moment it changes.",
        screenshotSlug: "schedule-grid",
      },
    ],
    related: ["in-house", "jobs"],
  },

  // ──────────────── PORTALS ────────────────
  {
    slug: "contracts",
    nav: "Contracts & Recurring Visits",
    category: "Core Ops",
    eyebrow: "Planned maintenance",
    title: "Recurring work that schedules itself.",
    subtitle:
      "Cleaning rounds, fire-alarm tests, grounds maintenance, PPM visits — set the contract up once and Fixlr generates every visit, assigns your crew, tracks completion and rolls the billing up per visit or per month. No spreadsheets, no missed rounds.",
    outcomes: [
      "Daily, weekly, fortnightly, monthly or ad-hoc schedules with weekday and time windows",
      "Visits auto-generated weeks ahead by a background scheduler — and regenerated when the contract changes",
      "Multi-operative crews per visit, with the same assign picker you use for jobs",
      "Per-visit or monthly invoicing, client PO numbers and rate basis (visit, hour, month) captured on the contract",
      "Clients can book extra visits themselves from the portal, inside your notice window",
      "Renewal and end-date tracking so nothing quietly lapses",
    ],
    sections: [
      {
        kind: "zig",
        headline: "One contract. Every visit on the calendar.",
        body: "Define the schedule — every Monday and Thursday 08:00–10:00, the 3rd of every month, ad-hoc on request — and Fixlr plans the visits for you. Each visit appears in the team schedule and the operative's day, with site contact, access notes and what3words location attached from the contract.",
        screenshotSlug: "contracts",
        screenshotCaption: "Contracts list — schedule, next visit, rate and status at a glance",
      },
      {
        kind: "zag",
        headline: "Completion, evidence and billing in the same thread.",
        body: "Operatives complete visits from their phone with photos and notes. Missed or late visits are flagged for the office, completed ones roll into the contract's invoicing run — per visit or a single monthly invoice — with the client's PO number already on it.",
        screenshotSlug: "schedule-grid",
        screenshotCaption: "Team schedule with contract visits alongside one-off jobs",
      },
      {
        kind: "zig",
        headline: "Let clients book, inside your rules.",
        body: "Turn on client booking per contract and set a minimum notice period. Clients raise an extra visit from their portal, it lands in your schedule as a pending visit, and your team confirms with one tap.",
        screenshotSlug: "client-portal-dashboard",
        screenshotCaption: "Client portal — contracts and upcoming visits visible to the client",
      },
    ],
    related: ["scheduling", "jobs", "client-portal", "invoicing"],
  },

  {
    slug: "in-house",
    nav: "In-House Team Portal",
    category: "Portals",
    eyebrow: "Your team, on the ground",
    title: "A PWA that turns your van into a command center.",
    subtitle:
      "Install Fixlr on any phone in 10 seconds. Push notifications for new jobs. Today's Snapshot at the top. Job sheets to tap through. Get Directions wired to Google Maps. Mark as Arrived logs the time.",
    outcomes: [
      "Install as PWA on iOS + Android — no app store needed",
      "Real-time push notifications for new job assignments",
      "`TodaysSnapshot` widget: Up Next / Done Today / Need Time Logged",
      "Morning briefing email at 06:30 local + EoD wrap at 18:00",
      "Tap-through job sheets with signature capture and photo upload",
      "Mark as Arrived auto-stamps time on site for billing accuracy",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Install it. Pin it. Go.",
        body: "PWA banner at the top of `/my-work` prompts operatives to install and enable push. No App Store review, no mobile dev team needed.",
        screenshotSlug: "in-house-pwa",
      },
      {
        kind: "zag",
        headline: "Today's Snapshot — one glance, three columns.",
        body: "Up next today, done today, need time logged. The same shape as the 06:30 briefing email. Auto-refreshes every 60 seconds, pauses in background.",
        screenshotSlug: "in-house-snapshot",
      },
      {
        kind: "zig",
        headline: "06:30 briefing + 18:00 EoD wrap.",
        body: "Your operatives wake up to a clean list of today's jobs with Open + Directions CTAs. At 18:00 they get a wrap summarising what's done and what needs a time log.",
        screenshotSlug: "in-house-briefing-email",
      },
    ],
    related: ["navigation", "jobs", "client-portal"],
  },

  {
    slug: "navigation",
    nav: "Navigation & Geo-tagging",
    category: "Portals",
    eyebrow: "Where is the team?",
    title: "Live team map with geo-tagged arrivals.",
    subtitle:
      "See every operative on a real map. Watch them arrive on site. Share a live ETA tracker with the client. Built into the same PWA — no extra apps.",
    outcomes: [
      "Live Team Map — real-time geo positions of on-shift operatives",
      "`MarkAsArrivedButton` stamps GPS + time when operative hits the doorstep",
      "Public `ShareTrackerButton` — client gets a live ETA link they can watch",
      "`GetDirectionsButton` deep-links to Google Maps / Apple Maps",
      "Auto-calculated on-site minutes roll straight into billing",
    ],
    sections: [
      {
        kind: "zig",
        headline: "One map. All your teams.",
        body: "Admins see every operative as a pin, colour-coded by status. Click a pin, see the job they're on. Click the job, jump to its detail page.",
        screenshotSlug: "live-team-map",
      },
      {
        kind: "zag",
        headline: "Client-facing live tracker.",
        body: "Toggle one switch, share a secure token link. The client watches the operative approach in real-time. Feels like Uber Eats for maintenance.",
        screenshotSlug: "nav-public-tracker",
      },
    ],
    related: ["in-house", "client-portal"],
  },

  {
    slug: "client-portal",
    nav: "Client Portal",
    category: "Portals",
    eyebrow: "Self-service, branded",
    title: "Clients raise requests. Approve quotes. Pay invoices. Without calling you.",
    subtitle:
      "Give every client a secure, white-labelled portal with real logins. They see their properties, live jobs, contracts, quotes and invoices; raise new requests 24/7 with photos; approve quotes online; and pay in one tap. Your team stops being the switchboard.",
    outcomes: [
      "Real client accounts with tiered access (full, view-only, property-limited) — invited from the client record",
      "Dashboard of live jobs, upcoming contract visits, open quotes and unpaid invoices",
      "Raise a request in 60 seconds with photos, urgency and property — it lands straight in your Requests queue",
      "Approve or decline quotes online with digital sign-off; you're alerted instantly",
      "Download invoices, see payment status and pay via Stripe from the email or the portal",
      "Property access notes, WhatsApp access and contractor visibility rules per client",
      "Your logo, your colours, your domain-style login page — clients never see 'Fixlr'",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Their whole estate, one login.",
        body: "The client dashboard shows everything happening across their properties — jobs in progress, the next scheduled contract visits, quotes waiting for a decision and invoices due. Every record they can see belongs to them: tenant isolation is enforced on every database query.",
        screenshotSlug: "client-portal-dashboard",
        screenshotCaption: "Client portal dashboard — jobs, visits, quotes and invoices for one client",
      },
      {
        kind: "zag",
        headline: "Requests in, phone calls out.",
        body: "A guided request form captures the property, what's wrong, urgency and photos. Requests arrive in your queue already categorised and matched to the right trade and area — with the client's contract and history attached.",
        screenshotSlug: "client-portal-requests",
        screenshotCaption: "Client requests view — status of everything they've raised",
      },
      {
        kind: "zig",
        headline: "Quotes approved, invoices paid — from anywhere.",
        body: "Clients approve quotes with one click and a digital signature, and see the resulting job appear. Invoices show live payment status, reminders go out automatically and Stripe pay links mean money arrives without a chase.",
        screenshotSlug: "client-portal-invoices",
        screenshotCaption: "Client invoices — download, payment status and one-tap pay",
      },
    ],
    related: ["contracts", "quotes", "invoicing", "white-label"],
  },

  {
    slug: "supplier-portal",
    nav: "Supplier Portal",
    category: "Supplier",
    eyebrow: "External vendors, managed",
    title: "A dedicated portal for every supplier you work with.",
    subtitle:
      "They log in, see their open jobs, submit quotes, upload completion docs, get paid. You approve, assign, compare — from a single dashboard.",
    outcomes: [
      "Per-supplier login with tenant-isolated view of their jobs only",
      "Submit quotes directly — no more email PDF back-and-forth",
      "Upload completion reports, photos, RAMS",
      "Track payments and download self-service statements",
      "En-route / Arrived / Complete status buttons (mobile-friendly)",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Suppliers are teammates, not email threads.",
        body: "Every vendor gets their own account with role-appropriate access. Jobs they can see, jobs they can quote, jobs they've completed — all on one screen.",
        screenshotSlug: "supplier-portal-dashboard",
      },
      {
        kind: "zag",
        headline: "En-route, arrived, complete — from the van.",
        body: "The same mobile UX as your in-house team. Suppliers feel like a natural extension of your operation.",
        screenshotSlug: "supplier-portal-mobile",
      },
    ],
    related: ["supplier-network", "supplier-compliance", "quotes"],
  },

  {
    slug: "supplier-network",
    nav: "Supplier Network",
    category: "Supplier",
    eyebrow: "Onboarding & marketplace",
    title: "Bulk-invite 100 suppliers in 10 minutes.",
    subtitle:
      "CSV upload, custom application forms, automated compliance checks, and a marketplace feed of available contractors. Scale your supplier base without an admin team.",
    outcomes: [
      "Bulk supplier invite (CSV + email templates)",
      "Public supplier-application page — they apply, you approve",
      "Trade / location / rating filters on the supplier directory",
      "`AcceptInvitePage` onboarding flow with doc upload",
      "Approval workflow with audit trail",
    ],
    sections: [
      {
        kind: "zig",
        headline: "CSV in. Supplier empire out.",
        body: "Drop a spreadsheet of 500 contractors and Fixlr emails each one a branded join link. They upload their docs. You approve. Live in a week.",
        screenshotSlug: "supplier-bulk-invite",
      },
      {
        kind: "zag",
        headline: "A public application page you actually want to show off.",
        body: "Point suppliers at `/apply`. They fill out your form, upload certs, land in your approval queue. Zero recruiter spam.",
        screenshotSlug: "supplier-application",
      },
    ],
    related: ["supplier-portal", "supplier-compliance", "ai-supplier-matching"],
  },

  {
    slug: "supplier-compliance",
    nav: "Supplier Compliance",
    category: "Supplier",
    eyebrow: "Sleep well at night",
    title: "Every cert. Every expiry. Every time.",
    subtitle:
      "Track insurance, RAMS, DBS, trade-specific certs per supplier. Auto-email 30 days before expiry. Block assignments when a cert lapses. Pass audits, every time.",
    outcomes: [
      "Per-supplier compliance doc library with expiry dates",
      "`compliance_scheduler` auto-emails 30/14/7/1 days before expiry",
      "Hard block: can't assign a job to a non-compliant supplier",
      "One-screen compliance dashboard across your entire supplier base",
      "Downloadable audit packs per supplier for insurance / procurement reviews",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Expiry alerts that actually fire.",
        body: "The compliance scheduler runs daily. Every supplier with a cert expiring in the next 30 days gets an automated branded email with a re-upload link. You're never the bottleneck.",
        screenshotSlug: "supplier-compliance-matrix",
      },
    ],
    related: ["supplier-portal", "supplier-network"],
  },

  {
    slug: "ai-supplier-matching",
    nav: "AI Supplier Matching",
    category: "Intelligence",
    eyebrow: "Intelligence layer",
    title: "Ask Fixlr: 'Who's the best plumber for this job?' — get an answer.",
    subtitle:
      "AI-powered supplier recommendations weighted by rating, response time, price history, compliance status, and locality. Stop hand-picking suppliers for every job.",
    outcomes: [
      "Weighted matching across 5+ criteria (rating, avg quote, SLA hit rate, distance, compliance)",
      "One-click assign of the top suggestion",
      "Reasoning surfaced — 'Why this supplier?' on every suggestion",
      "Learns from your accept/reject feedback over time",
      "Suggested reassignments when current pick is overloaded",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Not a chatbot. A decision co-pilot.",
        body: "AI Supplier Matching doesn't type at you — it ranks your shortlist. Distance-weighted, compliance-gated, price-sanity-checked. You click, it assigns.",
        screenshotSlug: "ai-supplier-match",
      },
      {
        kind: "zag",
        headline: "Suggested reassignments.",
        body: "When one supplier is overloaded and another is idle, Fixlr flags the imbalance on your dashboard with a 'Reassign?' suggestion.",
        screenshotSlug: "ai-reassign-suggest",
      },
    ],
    related: ["supplier-network", "supplier-portal", "emergency"],
  },

  // ──────────────── MANAGEMENT ────────────────
  {
    slug: "invoicing",
    nav: "Invoicing & Payments",
    category: "Management",
    eyebrow: "Money in",
    title: "Line-item invoices. Live totals. Stripe one-tap pay.",
    subtitle:
      "Generate branded invoices from completed jobs or build them manually. Line-item editor with live totals. Markup A (your margin) + Markup B (client uplift). Stripe integration for one-tap client payment.",
    outcomes: [
      "Manual or auto-generate from job/quote",
      "Editable line items with server-recomputed totals (anti-tamper)",
      "Two-layer markup system with custom labels",
      "Draft → Sent → Paid lifecycle with lock-on-sent",
      "Stripe pay-links on every invoice",
      "Branded PDF export",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Pull from the job. Edit before sending.",
        body: "Open the invoice dialog, pick the job, watch the line items auto-fill from the accepted quote. Tweak anything. Send.",
        screenshotSlug: "invoices-create",
      },
    ],
    related: ["jobs", "quotes"],
  },

  {
    slug: "assets",
    nav: "Asset Register",
    category: "Management",
    eyebrow: "Every boiler, every pump, every door",
    title: "Know what you own. Know when it breaks.",
    subtitle:
      "Register every asset (boilers, lifts, HVAC, fire-doors) against its property. Every job posts against an asset. See maintenance history, warranty expiry, replacement-cost projections.",
    outcomes: [
      "Unlimited assets per property with QR-code stickers",
      "Scan a QR → raise a job against that asset in 1 tap",
      "Full maintenance history per asset",
      "Predictive maintenance alerts based on service-interval rules",
      "Warranty + lifecycle tracking",
    ],
    sections: [
      {
        kind: "zig",
        headline: "QR stickers that turn every asset into a shortcut.",
        body: "Print QR codes. Slap them on every boiler. Operatives scan, Fixlr opens a pre-filled job form. Zero typing.",
        screenshotSlug: "assets-register",
      },
    ],
    related: ["jobs", "job-sheets"],
  },

  {
    slug: "job-sheets",
    nav: "Job Sheets & Forms",
    category: "Management",
    eyebrow: "Digital paperwork",
    title: "Every clipboard, gone. Every signature, captured.",
    subtitle:
      "Design custom job-sheet templates (gas safety, electrical test, fire inspection). Attach automatically to every job. Operatives fill them in the field. PDFs get stitched into the completion pack.",
    outcomes: [
      "Template designer — drag-and-drop form builder",
      "Auto-attach by job category (e.g. 'any gas job gets the gas safety sheet')",
      "Mobile-first filling — signature capture, photo upload, checkboxes",
      "Completion PDF auto-generated and attached to the job",
      "Shared across all operatives in real-time",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Build once, attach everywhere.",
        body: "The Job Sheet Template builder supports text, number, checkbox, dropdown, signature, photo. Assign to a category, done. Every job in that category auto-gets the sheet.",
        screenshotSlug: "job-sheets-builder",
      },
    ],
    related: ["jobs", "assets"],
  },

  {
    slug: "reports",
    nav: "Reports & Dashboards",
    category: "Management",
    eyebrow: "See everything",
    title: "The stats that matter, refreshed every minute.",
    subtitle:
      "Revenue, job volume, SLA hit-rate, first-time fix, vendor performance, operative utilisation. Export any chart to PDF or CSV. Scheduled weekly digest email.",
    outcomes: [
      "Live SaaS dashboard for super-admins (revenue, tenant growth, churn)",
      "Per-tenant operations dashboard (jobs, SLAs, supplier stats)",
      "Emergency response analytics",
      "Scheduled weekly digest email",
      "Export to PDF or CSV from every chart",
    ],
    sections: [
      {
        kind: "zig",
        headline: "The platform knows your numbers better than you do.",
        body: "Auto-refreshed every 60 seconds. Defensive nulls throughout — no white-screen-of-death when a chart has zero data.",
        screenshotSlug: "reports-dashboard",
      },
    ],
    related: ["jobs", "emergency"],
  },

  {
    slug: "white-label",
    nav: "White-label & Multi-tenant",
    category: "Management",
    eyebrow: "For platforms",
    title: "Resell Fixlr under your own brand.",
    subtitle:
      "Full tenant isolation. Custom domains (CNAME + TLS). Per-tenant logos, colours, email sender identities. Platform-admin layer for managing your downstream customers.",
    outcomes: [
      "Custom domain per tenant with auto TLS (CNAME verification baked in)",
      "Per-tenant brand colors, logo, email sender",
      "Platform admin SaaS dashboard (MRR, churn, active tenants)",
      "Tenant-level isolation enforced at every DB query",
      "White-glove onboarding: bulk-invite suppliers, import clients",
    ],
    sections: [
      {
        kind: "zig",
        headline: "Your brand. Our engine.",
        body: "Each tenant can point their own domain at Fixlr. Upload their logo. Set their brand colors. Emails go out from their domain. Clients never see 'Fixlr' anywhere.",
        screenshotSlug: "white-label-branding",
      },
    ],
    related: ["supplier-network", "integrations"],
  },

  {
    slug: "integrations",
    nav: "Integrations",
    category: "Management",
    eyebrow: "Plays nicely",
    title: "Slack. Teams. Stripe. SendGrid. Twilio. Google Maps. AI.",
    subtitle:
      "Webhooks in, webhooks out. Generic or Slack Block Kit or Teams MessageCard. Stripe pay-links. SendGrid email. Emergent LLM key for AI features (cost-controlled, centrally billed).",
    outcomes: [
      "Tenant-level webhook config — Generic / Slack / Teams",
      "Daily morning brief to Slack/Teams #operations channel at tenant local 06:30",
      "Stripe payment links on invoices",
      "SendGrid + SMTP email fallback",
      "Google Maps for addresses + directions",
      "Universal LLM key for AI supplier matching and chat",
    ],
    sections: [
      {
        kind: "zig",
        headline: "One webhook panel. Every destination.",
        body: "Pick your provider (generic / Slack / Teams), paste the URL, tick the events. We format the body for each platform — Block Kit for Slack, MessageCard for Teams.",
        screenshotSlug: "integrations-webhooks",
      },
    ],
    related: ["white-label", "emergency"],
  },

  {
    slug: "staff-monitoring",
    nav: "Staff Monitoring",
    category: "Management",
    eyebrow: "Team visibility",
    title: "Know your team is working — without hovering.",
    subtitle:
      "Live presence, time-on-task, screenshots on a cadence you control and a weekly productivity report — for office and remote staff alike. Built for Operations Directors who need evidence, not assumptions, and configured with your team's consent in mind.",
    outcomes: [
      "Live presence board: who's online, idle or offline right now, per team member",
      "Task timers tie hours to the jobs, quotes and requests they were spent on",
      "Optional periodic screenshots with configurable interval, blur and retention",
      "Weekly productivity report emailed to Operations Directors every Monday",
      "Role-gated: only Operations Directors can view monitoring data; staff can see their own",
      "Per-tenant settings — enable, tune or switch off monitoring for your whole company in one screen",
    ],
    sections: [
      {
        kind: "zig",
        headline: "The whole team on one board.",
        body: "The presence board shows every staff member's current state, active timer and last activity. Hover for the last hour, click for the day. When someone's been idle on an urgent request for too long, you see it before the client does.",
        screenshotSlug: "staff-monitoring",
        screenshotCaption: "Staff monitoring — live presence, timers and activity windows",
      },
      {
        kind: "zag",
        headline: "Evidence with guardrails.",
        body: "Screenshot capture is opt-in per tenant with a configurable interval, automatic blurring and a retention window. Everything is stored in your tenant's private bucket and is only visible to Operations Directors — never to other staff, suppliers or clients.",
        screenshotSlug: "reports-dashboard",
        screenshotCaption: "Weekly productivity report — hours by person, task and job",
      },
    ],
    related: ["in-house", "reports", "scheduling"],
  },

  {
    slug: "compliance-hub",
    nav: "Compliance Hub",
    category: "Management",
    eyebrow: "Single pane of glass",
    title: "Every certificate. One hub. Zero surprises.",
    subtitle:
      "Insurance, Gas Safe, NICEIC, RAMS — every supplier and job document tracked in one place with live expiry status, instant alerts the moment anything lapses, and one-click re-upload nudges to suppliers.",
    outcomes: [
      "One hub for supplier docs, job compliance and task certificates",
      "Traffic-light expiry status on every document, folder and supplier",
      "30/14/7-day reminders — then an instant admin ping the moment a doc expires",
      "Slack / Teams / email alerts via the `compliance.document_expired` webhook",
      "One-click re-upload links so suppliers fix gaps without logging in",
    ],
    sections: [
      {
        kind: "zig",
        headline: "The whole estate's paperwork on one screen.",
        body: "The Compliance Hub folders every document by supplier and type, flags what's expiring, what's missing, and what's already lapsed. Filters and search get you from 'are we covered?' to a definitive answer in seconds.",
        screenshotSlug: "compliance-hub",
        screenshotCaption: "Compliance Hub — folders, expiry traffic lights, re-upload nudges",
      },
      {
        kind: "zag",
        headline: "You find out the moment it lapses — not at the audit.",
        body: "A dedicated alert scheduler sweeps every 15 minutes. The instant a document crosses its expiry date, every admin gets an email, an in-app notification, and (if wired) a Slack or Teams ping — before the next job gets assigned to that supplier.",
        screenshotSlug: "supplier-compliance-matrix",
        screenshotCaption: "Supplier compliance matrix with live document status",
      },
    ],
    related: ["supplier-compliance", "supplier-network", "job-sheets"],
  },

  {
    slug: "route-planner",
    nav: "Route Planner",
    category: "Portals",
    eyebrow: "Field efficiency",
    title: "Plan the day. Drive less. Fix more.",
    subtitle:
      "Operatives plan their day in one tap — Fixlr orders the jobs, calculates drive legs between sites, and hands off turn-by-turn navigation. Less windscreen time, more wrench time.",
    outcomes: [
      "One-tap 'Plan my day' builds an ordered route from the day's jobs",
      "Leg-by-leg drive times and mileage between every site",
      "Turn-by-turn hand-off to Google Maps from each stop",
      "Mileage log rolls up automatically for expenses and reporting",
      "Admins see live progress on the Tracking Overview map",
    ],
    sections: [
      {
        kind: "zig",
        headline: "The day, in driving order.",
        body: "Pick the day and your start point — the planner sequences every scheduled job into an efficient route with drive-time estimates per leg. Emergencies slot in without wrecking the rest of the day.",
        screenshotSlug: "route-planner",
        screenshotCaption: "Operative route planner — ordered stops with drive legs",
      },
      {
        kind: "zag",
        headline: "Every mile accounted for.",
        body: "Each leg feeds the mileage log automatically, and the admin-side Tracking Overview shows who's where against plan. Expense claims and client billing stop being guesswork.",
        screenshotSlug: "live-team-map",
        screenshotCaption: "Live team map — operatives against their planned routes",
      },
    ],
    related: ["in-house", "navigation", "scheduling"],
  },
];

export const NAV_CATEGORIES = [
  { id: "Core Ops", blurb: "The daily operational engine" },
  { id: "Portals", blurb: "Who logs in to do what" },
  { id: "Supplier", blurb: "External vendor network" },
  { id: "Intelligence", blurb: "AI & automation" },
  { id: "Management", blurb: "Money, assets, compliance" },
];

export const BY_ROLE_PAGES = [
  {
    slug: "for-facilities-managers",
    nav: "For Facilities Managers",
    headline: "Run estate-wide maintenance like a single department.",
    subheadline:
      "One dashboard for every property, every asset, every job. Compliance packs that auditors love. SLA tracking that lets you walk into the board room confidently.",
    heroImage:
      "https://images.unsplash.com/photo-1748027869634-fc2e545cfb0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxmYWNpbGl0aWVzJTIwbWFuYWdlbWVudCUyMGJ1aWxkaW5nJTIwZmllbGQlMjBzZXJ2aWNlJTIwZW5naW5lZXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc3NjY2MTI2fDA&ixlib=rb-4.1.0&q=85",
    keyFeatures: ["jobs", "assets", "supplier-compliance", "reports", "emergency"],
  },
  {
    slug: "for-property-managers",
    nav: "For Property Managers",
    headline: "Less admin. Happier tenants. Healthier margins.",
    subheadline:
      "Turn inbound tenant complaints into billable work orders, assigned, completed, invoiced — without living in your inbox.",
    heroImage:
      "https://images.unsplash.com/photo-1730584474428-83f3cfc20a73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxmYWNpbGl0aWVzJTIwbWFuYWdlbWVudCUyMGJ1aWxkaW5nJTIwZmllbGQlMjBzZXJ2aWNlJTIwZW5naW5lZXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc3NjY2MTI2fDA&ixlib=rb-4.1.0&q=85",
    keyFeatures: ["client-portal", "jobs", "invoicing", "quotes"],
  },
  {
    slug: "for-field-service",
    nav: "For Field Service Businesses",
    headline: "Van → Job → Invoice. In three taps.",
    subheadline:
      "Purpose-built for crews of 3 to 300. PWA on every phone. Geo-tagged arrivals. Branded invoices the same hour the job completes.",
    heroImage:
      "https://images.unsplash.com/photo-1705579612181-faf45962cfcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxmYWNpbGl0aWVzJTIwbWFuYWdlbWVudCUyMGJ1aWxkaW5nJTIwZmllbGQlMjBzZXJ2aWNlJTIwZW5naW5lZXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc3NjY2MTI2fDA&ixlib=rb-4.1.0&q=85",
    keyFeatures: ["in-house", "navigation", "jobs", "invoicing"],
  },
  {
    slug: "for-partners",
    nav: "For MSPs & White-label Partners",
    headline: "Launch a maintenance SaaS this quarter.",
    subheadline:
      "Full white-label. Your domain. Your brand. Our engine. Resell Fixlr as your own product and own the full customer relationship.",
    heroImage:
      "https://images.unsplash.com/photo-1730584474428-83f3cfc20a73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxmYWNpbGl0aWVzJTIwbWFuYWdlbWVudCUyMGJ1aWxkaW5nJTIwZmllbGQlMjBzZXJ2aWNlJTIwZW5naW5lZXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzc3NjY2MTI2fDA&ixlib=rb-4.1.0&q=85",
    keyFeatures: ["white-label", "integrations", "ai-supplier-matching"],
  },
];

export function getFeatureBySlug(slug) {
  return featureManifest.find((f) => f.slug === slug);
}
